// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import TechnologyOutline from "@store/Library/Components/Icons/TechnologyOutline";
import TheRequirementsOutline from "@store/Library/Components/Icons/TheRequirementsOutline";
import BulbOutline from "@store/Library/Components/Icons/BulbOutline";
import CogwheelOutline from "@store/Library/Components/Icons/CogwheelOutline";
import ECommerceOutline from "@store/Library/Components/Icons/ECommerceOutline";
import MagnifierOutline from "@store/Library/Components/Icons/MagnifierOutline";
import NewspaperOutline from "@store/Library/Components/Icons/NewspaperOutline";
import RatingsOutline from "@store/Library/Components/Icons/RatingsOutline";
import WrenchOutline from "@store/Library/Components/Icons/WrenchOutline";
import { IconProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

const enum fields {
    icon = "icon",
    title = "title",
    description = "description",
    link = "link",
    linkText = "linkText",
    openInNewTab = "openInNewTab",
    customCSS = "customCSS",
    useBackground = "useBackground",
    backgroundColor = "backgroundColor",
    padding = "padding",
    borderRadius = "borderRadius",
}

const paddingField = getCssUnitField(fields.padding, { shorthands: ["top", "right", "bottom", "left"] });
const borderRadiusField = getCssUnitField(fields.borderRadius, {
    shorthands: ["top-left", "top-right", "bottom-right", "bottom-left"],
});

const enum icons {
    technologyOutline = "TechnologyOutline",
    theRequirementsOutline = "TheRequirementsOutline",
    eCommerceOutline = "ECommerceOutline",
    cogwheelOutline = "CogwheelOutline",
    bulbOutline = "BulbOutline",
    magnifierOutline = "MagnifierOutline",
    newspaperOutline = "NewspaperOutline",
    ratingsOutline = "RatingsOutline",
    wrenchOutline = "WrenchOutline",
}

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.technologyOutline:
            return TechnologyOutline;
        case icons.theRequirementsOutline:
            return TheRequirementsOutline;
        case icons.eCommerceOutline:
            return ECommerceOutline;
        case icons.cogwheelOutline:
            return CogwheelOutline;
        case icons.bulbOutline:
            return BulbOutline;
        case icons.magnifierOutline:
            return MagnifierOutline;
        case icons.newspaperOutline:
            return NewspaperOutline;
        case icons.ratingsOutline:
            return RatingsOutline;
        case icons.wrenchOutline:
            return WrenchOutline;
        default:
            return undefined;
    }
};

export interface QuickLinksTileProps extends WidgetProps {
    fields: {
        [fields.icon]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.link]: LinkFieldValue;
        [fields.linkText]: string;
        [fields.openInNewTab]: boolean;
        [fields.customCSS]: string;
        [fields.useBackground]: boolean;
        [fields.backgroundColor]: string;
        [fields.padding]: string;
        [fields.borderRadius]: string;
    };
}

export interface QuickLinksTileStyles {
    wrapper?: InjectableCss;
    iconRow?: InjectableCss;
    icon?: IconProps;
    body?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    linkRow?: InjectableCss;
    link?: LinkPresentationProps;
}

const quickLinksTileStyles: QuickLinksTileStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            width: 100%;
        `,
    },
    iconRow: {
        css: css`
            display: flex;
            align-items: flex-start;
        `,
    },
    icon: {
        css: css`
            & > svg {
                width: ${pxToRem(60)};
                height: ${pxToRem(60)};
                display: block;
            }
        `,
    },
    body: {
        css: css`
            display: grid;
            row-gap: ${pxToRem(16)};
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h3",
    },
    description: {
        variant: "p",
    },
    linkRow: {
        css: css`
            margin-top: auto;
            padding-top: ${pxToRem(40)};
        `,
    },
    link: linkGetActionLinkProps(ArrowForward, "right"),
};

const styles = quickLinksTileStyles;

const QuickLinksTile: FC<QuickLinksTileProps> = ({ fields }) => {
    const link = useGetLink(fields.link);
    const IconComponent = GetIcon(fields.icon);

    const hasTitle = Boolean(fields.title?.trim());
    const hasDescription = Boolean(fields.description?.trim());
    const hasLink = Boolean(link?.url);
    const linkText = (fields.linkText || "").trim() || "Learn more";

    const wrapperStyles = useMemo(() => {
        const parsedPadding = paddingField.parse(fields.padding);
        const parsedBorderRadius = borderRadiusField.parse(fields.borderRadius);

        return mergeProps(styles.wrapper, {
            css: css`
                ${styles.wrapper?.css}

                ${breakpointValues(parsedPadding, "padding")}
                ${breakpointValues(parsedBorderRadius, "border-radius")}

                ${({ theme }) =>
                    fields.useBackground
                        ? css`
                              background-color: ${resolveColor(fields.backgroundColor || "common.background", theme)};
                          `
                        : ""}

                ${fields.customCSS}
            `,
        });
    }, [fields.customCSS, fields.padding, fields.borderRadius, fields.useBackground, fields.backgroundColor]);

    if (!hasTitle && !hasDescription && !hasLink) {
        return null;
    }

    return (
        <StyledWrapper className="wrapper" {...wrapperStyles}>
            {!!IconComponent && (
                <StyledWrapper className="iconRow" {...styles.iconRow}>
                    <StyledWrapper className="icon" {...styles.icon} aria-hidden="true">
                        <IconComponent />
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper className="body" {...styles.body}>
                {hasTitle && (
                    <Typography className="title" {...styles.title}>
                        {fields.title}
                    </Typography>
                )}
                {hasDescription && (
                    <Typography className="description" {...styles.description}>
                        {fields.description}
                    </Typography>
                )}
            </StyledWrapper>
            {hasLink && (
                <StyledWrapper className="linkRow" {...styles.linkRow}>
                    <Link
                        className="link"
                        href={link!.url}
                        {...styles.link}
                        target={fields.openInNewTab ? "_blank" : undefined}
                        rel={fields.openInNewTab ? "noopener noreferrer" : undefined}
                        aria-label={fields.title ? `${fields.title} - ${linkText}` : linkText}
                    >
                        {linkText}
                    </Link>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const defaultCustomCss = `& {
}

.iconRow {
}

.icon {
}

.body {
}

.title {
}

.description {
}

.linkRow {
}

.link {
}
`;

const basicTab = { displayName: "Basic", sortOrder: 0 };
const styleTab = { displayName: "Style", sortOrder: 1 };
const advancedTab = { displayName: "Advanced", sortOrder: 2 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: QuickLinksTile,
    definition: {
        group: "Basic",
        icon: "link",
        displayName: "Quick Links Tile",
        fieldDefinitions: [
            {
                name: fields.icon,
                displayName: "Icon",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                hideEmptyOption: true,
                tab: basicTab,
                defaultValue: icons.technologyOutline,
                options: [
                    { displayName: "Technology Outline", value: icons.technologyOutline },
                    { displayName: "The Requirements Outline", value: icons.theRequirementsOutline },
                    { displayName: "E-Commerce Outline", value: icons.eCommerceOutline },
                    { displayName: "Cogwheel Outline", value: icons.cogwheelOutline },
                    { displayName: "Bulb Outline", value: icons.bulbOutline },
                    { displayName: "Magnifier Outline", value: icons.magnifierOutline },
                    { displayName: "Newspaper Outline", value: icons.newspaperOutline },
                    { displayName: "Ratings Outline", value: icons.ratingsOutline },
                    { displayName: "Wrench Outline", value: icons.wrenchOutline },
                ],
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "MultilineTextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "LinkField",
                fieldType: "Contextual",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.linkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
                tooltip: `Optional. Defaults to "Learn more".`,
            },
            {
                name: fields.openInNewTab,
                displayName: "Open link in new tab",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.useBackground,
                displayName: "Use Background",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                tab: styleTab,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "common.background",
                tab: styleTab,
                isVisible: x => !!x.fields.useBackground,
            },
            {
                ...paddingField.definition,
                displayName: "Padding",
                fieldType: "Contextual",
                defaultValue: "",
                tab: styleTab,
            },
            {
                ...borderRadiusField.definition,
                displayName: "Border Radius",
                fieldType: "Contextual",
                defaultValue: "",
                tab: styleTab,
            },

            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) return "";
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors?.length) {
                        return result.stylesheet.parsingErrors.map(e => `${e.reason} on line ${e.line}`)[0];
                    }
                    return "";
                },
                options: { mode: "css", lint: true, autoRefresh: true },
            },
        ],
    },
};

export default widgetModule;
